<app-admin-modal title="Merge Leads" size="2xl" (closed)="close()">
  @if (loading()) {
    <p class="cg-form-help">Loading merge preview…</p>
  } @else if (error() && !preview()) {
    <p class="cg-form-help cg-form-help--error" role="alert">{{ error() }}</p>
    <div class="lead-merge-modal__actions">
      <app-admin-button variant="secondary" type="button" (click)="close()">Cancel</app-admin-button>
      <app-admin-button variant="primary" type="button" (click)="loadPreview()">Retry</app-admin-button>
    </div>
  } @else {
    @let data = preview();
    @if (!data) {
      <app-admin-empty-state
        icon="users"
        title="No leads to merge"
        description="Select at least two leads to merge."
      />
    } @else {
      <p class="cg-form-help lead-merge-modal__intro">
        Combine duplicate leads into one master lead. Your selected master will remain active, and the
        other leads will be merged into it.
      </p>

      <nav class="lead-merge-modal__steps" aria-label="Merge steps">
        @for (item of [1, 2, 3, 4]; track item) {
          <span
            class="lead-merge-modal__step-dot"
            [class.lead-merge-modal__step-dot--active]="step() === item"
            [class.lead-merge-modal__step-dot--done]="step() > item"
          >
            {{ item }}
          </span>
        }
      </nav>

      <h3 class="lead-merge-modal__heading">{{ stepTitle() }}</h3>

      @if (blockers().length > 0) {
        <div class="lead-merge-modal__alerts lead-merge-modal__alerts--danger" role="alert">
          <p class="lead-merge-modal__alerts-title">This merge cannot continue</p>
          @for (blocker of blockers(); track blocker.code + blocker.message) {
            <p>{{ blocker.message }}</p>
          }
        </div>
      }

      @if (step() >= 3 && warnings().length > 0) {
        <div class="lead-merge-modal__alerts lead-merge-modal__alerts--warn" role="status">
          <p class="lead-merge-modal__alerts-title">Please review before confirming</p>
          @for (warning of warnings(); track warning.code + warning.message) {
            <p>{{ warning.message }}</p>
          }
        </div>
      }

      <div class="lead-merge-modal__body">
        @if (step() === 1) {
          <p class="cg-form-help lead-merge-modal__hint">
            Select the lead that should remain active. The other leads will be merged into this one.
          </p>
          <div class="lead-merge-modal__master-list">
            @for (lead of data.leads; track lead._id) {
              <label
                class="lead-merge-modal__master-card"
                [class.lead-merge-modal__master-card--selected]="isMaster(lead)"
              >
                <input
                  type="radio"
                  name="masterLead"
                  [value]="lead._id"
                  [checked]="isMaster(lead)"
                  (change)="setMaster(lead._id)"
                />
                <div class="lead-merge-modal__master-card-body">
                  <div class="lead-merge-modal__master-card-top">
                    <strong>{{ lead.fullName }}</strong>
                    @if (isMaster(lead)) {
                      <span class="lead-merge-modal__badge">Master Lead — This Lead will remain active</span>
                    }
                  </div>
                  <div class="cg-form-help">
                    {{ lead.phoneNumber }}
                    @if (lead.email) {
                      · {{ lead.email }}
                    }
                  </div>
                  <div class="cg-form-help">
                    Status: {{ lead.status.name || '—' }}
                    @if (lead.createdAt) {
                      · Created {{ lead.createdAt | date: 'mediumDate' }}
                    }
                  </div>
                  @if (lead.hasApplicant) {
                    <div class="cg-form-help">Linked applicant</div>
                  }
                </div>
              </label>
            }
          </div>
          @if (masterLead()) {
            <p class="cg-form-help lead-merge-modal__hint">
              The other {{ sourceLeadCount() }} lead{{ sourceLeadCount() === 1 ? '' : 's' }} will be
              merged into <strong>{{ masterLead()!.fullName }}</strong>.
            </p>
          }
        }

        @if (step() === 2) {
          @if (conflicts().length === 0) {
            <p class="cg-form-help">
              No conflicting information. Matching values will be kept automatically.
            </p>
          } @else {
            <p class="cg-form-help lead-merge-modal__hint">
              These fields have different values. Choose the value to keep for each one.
            </p>
            <div class="lead-merge-modal__conflicts">
              @for (conflict of conflicts(); track conflict.field) {
                <div class="lead-merge-modal__conflict">
                  <div class="lead-merge-modal__conflict-label">
                    {{ conflictPrompt(conflict.field) }}
                  </div>
                  <div class="lead-merge-modal__conflict-options">
                    @for (lead of data.leads; track lead._id) {
                      <label
                        class="lead-merge-modal__option"
                        [class.lead-merge-modal__option--selected]="
                          fieldSelections()[conflict.field] === lead._id
                        "
                      >
                        <input
                          type="radio"
                          [name]="'field-' + conflict.field"
                          [value]="lead._id"
                          [checked]="fieldSelections()[conflict.field] === lead._id"
                          (change)="setFieldSelection(conflict.field, lead._id)"
                        />
                        <span>
                          <span class="lead-merge-modal__option-value">
                            {{ displayValue(conflict.field, conflict.valuesByLeadId[lead._id] ?? null) }}
                          </span>
                          <span class="cg-form-help">From {{ lead.fullName }}</span>
                        </span>
                      </label>
                    }
                  </div>
                </div>
              }
            </div>
          }

          @if (autoResolved().length > 0) {
            <div class="lead-merge-modal__auto">
              <button
                type="button"
                class="lead-merge-modal__toggle"
                (click)="showAutoResolved.set(!showAutoResolved())"
                [attr.aria-expanded]="showAutoResolved()"
              >
                Other information — automatically resolved ({{ autoResolved().length }})
                <span aria-hidden="true">{{ showAutoResolved() ? '▾' : '▸' }}</span>
              </button>
              @if (showAutoResolved()) {
                <ul class="lead-merge-modal__auto-list">
                  @for (item of autoResolved(); track item.field) {
                    <li>
                      <strong>{{ fieldLabel(item.field) }}:</strong>
                      {{ displayValue(item.field, item.value) }}
                      <span class="cg-form-help">from {{ leadLabel(item.sourceLeadId) }}</span>
                    </li>
                  }
                </ul>
              }
            </div>
          }
        }

        @if (step() === 3) {
          <p class="cg-form-help lead-merge-modal__hint">
            These records will be connected to the Master Lead. Nothing will be deleted.
          </p>
          <div class="lead-merge-modal__related">
            <div class="lead-merge-modal__related-row">
              <span>Notes</span><strong>{{ data.relatedCountsTotal.notes }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Activities</span><strong>{{ data.relatedCountsTotal.activities }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Tasks</span><strong>{{ data.relatedCountsTotal.tasks }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Communications</span><strong>{{ data.relatedCountsTotal.communications }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Notifications</span><strong>{{ data.relatedCountsTotal.notifications }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Applicants</span><strong>{{ data.relatedCountsTotal.applicants }}</strong>
            </div>
          </div>
          <p class="cg-form-help">
            Total related records to connect:
            <strong>{{ relatedRecordsTotal() }}</strong>
          </p>
        }

        @if (step() === 4) {
          <div class="lead-merge-modal__summary-card">
            <div class="lead-merge-modal__related-row">
              <span>Master Lead</span>
              <strong>{{ masterLead()?.fullName || '—' }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Leads being merged</span>
              <strong>{{ sourceLeadCount() }}</strong>
            </div>
            <div class="lead-merge-modal__related-row">
              <span>Related records moved</span>
              <strong>{{ relatedRecordsTotal() }}</strong>
            </div>
          </div>

          <label class="lead-merge-modal__confirm">
            <input
              type="checkbox"
              [checked]="confirmed()"
              (change)="confirmed.set($any($event.target).checked)"
            />
            <span>
              I understand that the other leads will be merged into the Master Lead and will no longer
              appear in the Leads list.
            </span>
          </label>
        }
      </div>

      @if (error()) {
        <p class="cg-form-help cg-form-help--error" role="alert">{{ error() }}</p>
      }

      <div class="lead-merge-modal__actions">
        <app-admin-button variant="secondary" type="button" [disabled]="submitting()" (click)="close()">
          Cancel
        </app-admin-button>
        @if (step() > 1) {
          <app-admin-button variant="secondary" type="button" [disabled]="submitting()" (click)="goBack()">
            Back
          </app-admin-button>
        }
        @if (step() < 4) {
          <app-admin-button
            variant="primary"
            type="button"
            [disabled]="!canContinue()"
            (click)="goContinue()"
          >
            Continue
          </app-admin-button>
        } @else {
          <app-admin-button
            variant="primary"
            type="button"
            [disabled]="!canSubmit()"
            (click)="confirmMerge()"
          >
            {{ submitting() ? 'Merging…' : 'Merge Leads' }}
          </app-admin-button>
        }
      </div>
    }
  }
</app-admin-modal>
